import {
  type FC, useCallback, useMemo, useState,
} from 'react';
import { CONFIG } from '~/_shared/constants/config';
import { TwoFactorMode } from '~/_shared/constants/twoFactorMode.enum';
import {
  generateCodeChallenge, generateCodeVerifier, generateSecureString,
} from '~/_shared/utils/crypto';
import { getTeamMemberError } from '~/clientTeamManagement/clientTeamManagement.helpers';
import { sendTwoFactorEmailRequest } from '~/store/userData/repository/userData.repository';
import { useUserDataSelector } from '~/store/userData/userData.selectors';
import { useAuthContext } from '../../authContext';
import {
  GOOGLE_SECURITY_TOKEN_ITEM_KEY, GOOGLE_SECURITY_TOKEN_KEY,
  GOOGLE_SSO_PROMPT,
  GOOGLE_SSO_SCOPES,
} from '../sso/googleSsoPage.container';
import {
  MS_PKCE_CODE_VERIFIER_KEY,
  MS_SECURITY_TOKEN_ITEM_KEY,
  MS_SECURITY_TOKEN_KEY,
  MS_SSO_SCOPES,
} from '../sso/msSsoPage.container';
import {
  getGoogleSsoRedirectUri, getMsSsoRedirectUri,
} from '../sso/sso.helpers';
import { Login2FaFormComponent } from './login2FaForm.component';
import { LoginFormComponent } from './loginForm.component';
import { useUserSign } from './useUserSign';

type LoginFormContainerProps = {
  onLoginFormSubmit?: () => void;
};

export const LoginFormContainer: FC<LoginFormContainerProps> = (props) => {
  const { isLoading, error, mfaMode, availableMfaModes, authenticateUserRequest, authenticate2FaUserRequest,
    resetTwoFactorData, changeMfaMode } = useUserSign();
  const { error: refreshTokenError, clearError } = useAuthContext();
  const teamMemberError = getTeamMemberError();
  const [usedEmail, setUsedEmail] = useState('');
  const [usedPassword, setUsedPassword] = useState('');
  const [isGeneratingCodeChallenge, setIsGeneratingCodeChallenge] = useState(false);
  const isLoadingFromState = useUserDataSelector().isLoading;

  const dispatchProps = useMemo(() => {
    return ({
      onFormSubmit: async (email: string, password: string, rememberMe: boolean) => {
        clearError();
        setUsedEmail(email);
        setUsedPassword(password);
        await authenticateUserRequest({ email, password, rememberMe });

        const onLoginFormSubmit = props.onLoginFormSubmit;
        onLoginFormSubmit?.();
      },
      on2FASubmit: async (code: string) => {
        clearError();
        await authenticate2FaUserRequest(code);
      },
      reset2FaMode: () => {
        resetTwoFactorData();
      },
    });
  }, [authenticate2FaUserRequest, authenticateUserRequest, clearError, props.onLoginFormSubmit, resetTwoFactorData]);

  const resendMFAEmail = useCallback(() =>
    sendTwoFactorEmailRequest(usedEmail, usedPassword),
  [usedEmail, usedPassword]);

  const onLegacyLogin = useCallback(() => {
    location.href = CONFIG.LEGACY_LOGIN_URL;
  }, []);

  const onGoogleLogin = useCallback(() => {
    const clientId = CONFIG.GOOGLE_OAUTH_CLIENT_ID;
    const redirectUri = getGoogleSsoRedirectUri();
    const scope = GOOGLE_SSO_SCOPES;
    const secureString = generateSecureString();
    sessionStorage.setItem(GOOGLE_SECURITY_TOKEN_ITEM_KEY, secureString);
    const state = encodeURIComponent(JSON.stringify({ [GOOGLE_SECURITY_TOKEN_KEY]: secureString }));

    const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?${new URLSearchParams({
      client_id: clientId,
      redirect_uri: redirectUri,
      response_type: 'code',
      scope,
      state,
      access_type: 'offline',
      prompt: GOOGLE_SSO_PROMPT,
    }).toString()}`;

    window.location.href = authUrl;
  }, []);

  const onMicrosoftLogin = useCallback(async () => {
    const clientId = CONFIG.MS_OAUTH_CLIENT_ID || '';
    const redirectUri = getMsSsoRedirectUri();
    const secureString = generateSecureString();
    sessionStorage.setItem(MS_SECURITY_TOKEN_ITEM_KEY, secureString);
    const state = encodeURIComponent(JSON.stringify({ [MS_SECURITY_TOKEN_KEY]: secureString }));

    // PKCE implementation
    const codeVerifier = generateCodeVerifier();

    setIsGeneratingCodeChallenge(true);
    const codeChallenge = await generateCodeChallenge(codeVerifier);
    setIsGeneratingCodeChallenge(false);

    // Store code verifier in sessionStorage to use during token exchange
    sessionStorage.setItem(MS_PKCE_CODE_VERIFIER_KEY, codeVerifier);

    const authUrl = `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?${new URLSearchParams({
      tenant: 'common',
      client_id: clientId,
      redirect_uri: redirectUri,
      response_type: 'code',
      scope: MS_SSO_SCOPES,
      state,
      prompt: 'consent',
      code_challenge: codeChallenge,
      code_challenge_method: 'S256', // SHA-256 hashing method
    }).toString()}`;

    window.location.href = authUrl;
  }, []);

  const isAnythingLoading = isLoading || isLoadingFromState || isGeneratingCodeChallenge;

  return (
    <div>
      {!mfaMode ? (
        <LoginFormComponent
          error={error || refreshTokenError || teamMemberError || ''}
          isLoading={isAnythingLoading}
          onSubmit={dispatchProps.onFormSubmit}
          onGoogleLogin={onGoogleLogin}
          onMicrosoftLogin={onMicrosoftLogin}
          onLegacyLogin={onLegacyLogin}
        />
      ) : (
        <Login2FaFormComponent
          error={error || refreshTokenError || ''}
          isLoading={isAnythingLoading}
          mfaMode={mfaMode}
          availableMfaModes={availableMfaModes}
          onSubmit={dispatchProps.on2FASubmit}
          repeatMFA={mfaMode === TwoFactorMode.google ? undefined : resendMFAEmail}
          reset2FaMode={dispatchProps.reset2FaMode}
          onChangeMfaMode={changeMfaMode}
        />
      )}
    </div>
  );
};
